import React, { useMemo, useEffect, useState, useCallback } from 'react';
import {
  AiOutlineDoubleLeft,
  AiOutlineLeft,
  AiOutlineRight,
  AiOutlineDoubleRight,
} from 'react-icons/ai';
import { IoIosArrowDown } from 'react-icons/io';
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  Button,
  HStack,
  Select,
  Text,
  IconButton,
  VStack,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { useAppSelector } from '@spartalabs/pdc-core';
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  ColumnDef,
  PaginationState,
  getExpandedRowModel,
  ExpandedState,
} from '@tanstack/react-table';
import { ErrorCard } from 'components/Cards/ErrorCard';
import { Loading } from 'components/Loading';
import { TableSkeleton } from 'components/Skeletons/DataTable';
import Tag from 'components/Tag';
import { useAnalyticsEvent } from 'hooks';
import { GA_TABLE_LABEL } from 'utils/constants';

export const DataTable: React.FC<DataTableProps> = ({
  labels,
  isPaginated = true,
  data,
  customColumns = null,
  loading,
  fetchData,
  searchTerm,
  costCenter,
  region,
  startDate,
  endDate,
  isError,
  costCenters,
  analyticsId,
  error,
  cc,
  openToken,
  hasApprovers,
  filters,
  searchPlates,
  totalLines,
  ...rest
}) => {
  const currentDivisionId = useAppSelector(
    state => state.authSlicer.user.currentDivisionId,
  );

  const [sorting, setSorting] = useState<
    { column: number; direction: string } | undefined
  >();

  const [expanded, setExpanded] = useState<ExpandedState>({});

  const analyticsEvent = useAnalyticsEvent({
    category: 'User',
    action: GA_TABLE_LABEL,
  });

  const [{ pageIndex, pageSize }, setPagination] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: 5,
    });

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [data],
  );

  const handleDynamicPage = useCallback(
    (rows: number, lines?: number) => {
      if (!rows) return '';

      const size = lines || pageSize;

      const _pages = table.getState().pagination.pageIndex * size + 1;

      const totalDisplay = (table.getState().pagination.pageIndex + 1) * size;

      const displayCount = totalDisplay < 40 ? totalDisplay : rows;

      return `Mostrando de ${_pages} até ${displayCount} de ${rows} registros`;
    },
    [pageSize],
  );

  const shouldDouble = useMemo(() => {
    if (totalLines) return totalLines > 5;
    else false;
  }, [totalLines]);

  const defaultColumns = useMemo(() => {
    if (labels && labels.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dataColumns: ColumnDef<Data, any>[] = labels.map(label => ({
        header: label.title,
        accessorKey: label.accessor,
        enableSorting: !label.disableSorting,
        cell: info =>
          label.type === 'button' ? (
            <Button
              onClick={() =>
                label.ButtonFn &&
                label.ButtonFn({
                  id: info.row.getValue('id'),
                })
              }
            >
              {info.getValue()}
            </Button>
          ) : label.type === 'accordion' ? (
            info.row.getCanExpand() ? (
              <Flex alignItems="center">
                <Icon
                  ml="-24px"
                  as={IoIosArrowDown}
                  onClick={info.row.getToggleExpandedHandler()}
                  h="14px"
                  w="14px"
                  transition="all .5s ease"
                  mr="10px"
                  transform={
                    info.row.getIsExpanded()
                      ? 'rotate(-180deg)'
                      : 'rotate(-360deg)'
                  }
                />
                {info.getValue()}
              </Flex>
            ) : (
              <Text
                pl={info.row.depth > 0 ? '12px' : '0px'}
                textColor="text.100"
              >
                {info.getValue()}
              </Text>
            )
          ) : label.type === 'tag' ? (
            <Tag label={info.getValue()} />
          ) : label.type === 'tagCustom' ? (
            <Tag
              label={info.getValue().title}
              customBg={info.getValue().bg}
              customText={info.getValue().color}
            />
          ) : label.type === 'custom' ? (
            label.component &&
            label.component(info.row.getValue('id'), costCenter)
          ) : label.type === 'customValue' ? (
            label.component &&
            label.component(info.getValue(), info.row.getValue('id'))
          ) : (
            <Text
              fontSize="12px"
              minW={info?.getValue()?.length * 6 + 'px'}

              textColor="text.100"
            >
              {info.getValue()}
            </Text>
          ),
      }));

      const columns = [...dataColumns];

      return columns;
    }

    return [];
  }, [labels, costCenter]);

  const hiddenColumns =
    labels && labels.length > 0
      ? Object.fromEntries(
          new Map(labels.map(label => [label.accessor, label.isShown ?? true])),
        )
      : {};

  const table = useReactTable({
    columns: customColumns ?? defaultColumns,
    data: data?.itens || [],
    pageCount: data?.pageCount,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    getSubRows: row => row.subRows,
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    manualPagination: true,
    initialState: {
      columnVisibility: { ...hiddenColumns },
      pagination: { pageSize: isPaginated ? 5 : 9999 },
    },
    state: {
      pagination,
      expanded,
    },
  });

  const handleFetch = () => {
    if (fetchData) {
      const params: FetchDataParams = {
        page: pageIndex + 1,
        quantity: pageSize,
        term: searchTerm,
        region,
        sorting,
        startDate,
        endDate,
        searchPlates,
        cc,
      };

      if (filters) {
        Object.keys(filters).forEach(key => {
          const filterValue = filters[key].state.value;
          if (filterValue) {
            params[key] = filterValue;
          }
        });
      }

      if (
        costCenter &&
        costCenters?.flatMap(item => item.name).includes(costCenter.name)
      ) {
        params.selected = costCenter.id || costCenters[0].id;
      }

      fetchData(params);
    }
  };

  useEffect(() => {
    handleFetch();
  }, [
    costCenter?.id,
    costCenters,
    searchTerm,
    startDate,
    endDate,
    pageIndex,
    pageSize,
    sorting,
    currentDivisionId,
    cc,
    filters,
    searchPlates,
  ]);

  useEffect(() => {
    setPagination(prev => ({ pageIndex: 0, pageSize: prev.pageSize }));
  }, [searchTerm, startDate, endDate, currentDivisionId]);

  useEffect(() => {
    table.setPageIndex(0);
  }, [fetchData]);

  if (isError)
    return (
      <ErrorCard
        hasApprovers={hasApprovers}
        openToken={openToken}
        error={error}
        shadow={0}
        border="0px"
        {...rest}
        refetch={handleFetch}
      />
    );

  return (
    <VStack w={rest.w} h={rest.h} spacing="24px" align="start">
      <Flex
        w="100%"
        overflow="auto"
        borderLeft="1px solid"
        borderRight="1px solid"
        borderColor="grey.200"
      >
        {!data ? (
          <Flex overflowX="auto" position="relative" w="100%">
            <TableSkeleton labels={labels} />
          </Flex>
        ) : (
          <Flex w="100%" position="relative">
            {loading && (
              <Flex justify="center" position="absolute" w="100%" h="100%">
                <Loading />
              </Flex>
            )}
            <Table opacity={loading ? '50%' : '100%'} w="100%">
              <Thead bg="grey.400">
                {table.getHeaderGroups().map(headerGroup => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => {
                      // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const meta: any = header.column.columnDef.meta;
                      return (
                        <Th
                          fontWeight={400}
                          fontSize="12px"
                          color="black"
                          borderColor="grey.200"
                          key={header.id}
                          onClick={() => {
                            header.column.getCanSort() &&
                              setSorting(prev => ({
                                column: index,
                                direction:
                                  prev?.column !== index
                                    ? 'asc'
                                    : prev.direction === 'asc'
                                    ? 'desc'
                                    : 'asc',
                              }));
                          }}
                          isNumeric={meta?.isNumeric}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}

                          <chakra.span pl="4">
                            {sorting?.column === index ? (
                              sorting.direction === 'desc' ? (
                                <TriangleDownIcon aria-label="ordenado descendente" />
                              ) : (
                                <TriangleUpIcon aria-label="ordenado ascendente" />
                              )
                            ) : null}
                          </chakra.span>
                        </Th>
                      );
                    })}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {data && data.itens.length > 0 ? (
                  table.getRowModel().rows.map(row => (
                    <Tr
                      key={row.id}
                      borderLeft="1px solid"
                      borderRight="1px solid"
                      borderColor="grey.200"
                      bg={
                        row.getIsExpanded() || row.depth > 0
                          ? 'background'
                          : 'transparent'
                      }
                    >
                      {row.getVisibleCells().map(cell => {
                        // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const meta: any = cell.column.columnDef.meta;
                        return (
                          <Td
                            borderColor="grey.200"
                            key={cell.id}
                            isNumeric={meta?.isNumeric}
                            fontSize="12px"
                            color="grey.600"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                  ))
                ) : (
                  <Tr
                    borderLeft="1px solid"
                    borderRight="1px solid"
                    borderColor="grey.200"
                  >
                    <Td
                      colSpan={labels && labels.length}
                      borderColor="grey.200"
                      fontSize="12px"
                      color="grey.600"
                      textAlign="center"
                    >
                      Não foram encontradas informações
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Flex>
        )}
      </Flex>
      <HStack
        flexDir={{ base: 'column', sm: 'row' }}
        w="100%"
        justify="space-between"
      >
        <Text color="text.300" fontSize="14px">
          {handleDynamicPage(data?.rows || 0, totalLines)}
        </Text>
        {isPaginated && (
          <Flex>
            <Select
              value={table.getState().pagination.pageSize}
              onChange={e => {
                table.setPageSize(Number(e.target.value));
              }}
              color="grey.600"
              aria-label="Quantidade de linhas"
            >
              {[5, 10, 15, 20, 25].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  {shouldDouble ? pageSize * 2 : pageSize} Linhas
                </option>
              ))}
            </Select>

            <IconButton
              icon={<AiOutlineDoubleLeft />}
              aria-label="Primeira página"
              variant="ghost"
              color="grey.600"
              onClick={() => {
                analyticsEvent(`'Navegou na tabela de ${analyticsId}`);
                table.setPageIndex(0);
              }}
              isDisabled={loading || !table.getCanPreviousPage()}
            />
            <IconButton
              icon={<AiOutlineLeft />}
              aria-label="Voltar página"
              variant="ghost"
              color="grey.600"
              onClick={() => {
                analyticsEvent(`'Navegou na tabela de ${analyticsId}`);
                table.previousPage();
              }}
              isDisabled={loading || !table.getCanPreviousPage()}
            />
            <IconButton
              icon={<AiOutlineRight />}
              aria-label="Avançar página"
              variant="ghost"
              color="grey.600"
              onClick={() => {
                analyticsEvent(`'Navegou na tabela de ${analyticsId}`);
                table.nextPage();
              }}
              isDisabled={loading || !table.getCanNextPage()}
            />
            <IconButton
              icon={<AiOutlineDoubleRight />}
              aria-label="Última página"
              variant="ghost"
              color="grey.600"
              onClick={() => {
                analyticsEvent(`'Navegou na tabela de ${analyticsId}`);
                table.setPageIndex(table.getPageCount);
              }}
              isDisabled={loading || !table.getCanNextPage()}
            />
          </Flex>
        )}
      </HStack>
    </VStack>
  );
};
